// src/components/views/CustomersView.jsx
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import Pagination from '../Pagination';
import { formatCurrency } from '../../utils/formatters';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const CustomersView = ({ customers, sales, openModal, onCustomerSelect, selectedIds, onSelectAll, onSelectOne, onDeleteSelected, scrollTo, onScrollComplete, userId, userSettings }) => {
    const tableRef = useRef(null);
    
    // Lógica do Link de Auto-Cadastro
    const [isLinkActive, setIsLinkActive] = useState(userSettings?.isLandingPageActive ?? true);
    const [copiado, setCopiado] = useState(false);

    const handleToggleLink = async (newValue) => {
        try {
            const userRef = doc(db, 'users', userId);
            await updateDoc(userRef, { isLandingPageActive: newValue });
            setIsLinkActive(newValue);
        } catch (error) {
            console.error("Erro ao atualizar status do link:", error);
            alert("Não foi possível alterar o status do link.");
        }
    };

    const handleShareWhatsApp = async () => {
    const shareLink = `${window.location.origin}/cadastro/${userId}`;
    const message = `Olá! Quero te conhecer melhor. Por favor, preencha seus dados aqui: ${shareLink}`;

    try {
        await navigator.clipboard.writeText(message);
        setCopiado(true);
        setTimeout(() => setCopiado(false), 2000);
    } catch (err) {
        console.error('Erro ao copiar texto: ', err);
    }
};

    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sort, setSort] = useState({ key: 'name', direction: 'asc' });
    
    const [birthdayPage, setBirthdayPage] = useState(1);
    const birthdaysPerPage = 3;

    useEffect(() => {
        if (scrollTo === 'gestao-clientes') {
            tableRef.current?.scrollIntoView({ behavior: 'smooth' });
            onScrollComplete(); 
        }
    }, [scrollTo, onScrollComplete]);

    const { topCustomers, upcomingBirthdays } = useMemo(() => {
        if (!customers || !customers.length) {
            return { topCustomers: [], topProducts: [], upcomingBirthdays: [] };
        }

        const customerRevenue = new Map();
        const productQuantities = new Map();

        (sales || []).forEach(sale => {
            if (sale.customerId && !sale.isGift) {
                const currentRevenue = customerRevenue.get(sale.customerId) || 0;
                customerRevenue.set(sale.customerId, currentRevenue + (sale.totalAmount || 0));
            }
            (sale.items || []).forEach(item => {
                 const currentQty = productQuantities.get(item.productName) || 0;
                 productQuantities.set(item.productName, currentQty + item.quantity);
            });
        });
        
        const rankedCustomers = Array.from(customerRevenue.entries())
            .map(([customerId, totalRevenue]) => {
                const customer = customers.find(c => c.id === customerId);
                return {
                    id: customerId,
                    name: customer ? customer.name : 'Cliente Desconhecido',
                    totalRevenue: totalRevenue
                };
            })
            .sort((a, b) => b.totalRevenue - a.totalRevenue)
            .slice(0, 10);
            
        const rankedProducts = Array.from(productQuantities.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 10);
        
        const today = new Date();
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(today.getDate() + 30);

        const upcomingBirthdays = customers
            .filter(customer => {
                if (!customer.birthDate) return false;
                const birthDate = new Date(customer.birthDate);
                birthDate.setFullYear(today.getFullYear());

                if (birthDate < today) {
                    birthDate.setFullYear(today.getFullYear() + 1);
                }
                
                return birthDate >= today && birthDate <= thirtyDaysFromNow;
            })
            .sort((a, b) => {
                const dateA = new Date(a.birthDate);
                dateA.setFullYear(today.getFullYear());
                if(dateA < today) dateA.setFullYear(today.getFullYear() + 1);

                const dateB = new Date(b.birthDate);
                dateB.setFullYear(today.getFullYear());
                if(dateB < today) dateB.setFullYear(today.getFullYear() + 1);

                return dateA - dateB;
            });

        return { topCustomers: rankedCustomers, topProducts: rankedProducts, upcomingBirthdays };
    }, [customers, sales]);

    const paginatedBirthdays = useMemo(() => {
        const startIndex = (birthdayPage - 1) * birthdaysPerPage;
        return upcomingBirthdays.slice(startIndex, startIndex + birthdaysPerPage);
    }, [upcomingBirthdays, birthdayPage, birthdaysPerPage]);


    const filteredAndSortedCustomers = useMemo(() => {
        let items = customers.map(customer => {
            const customerSales = sales.filter(sale => sale.customerId === customer.id);
            let paymentStatus = 'Sem Compras';
            if (customerSales.length > 0) {
                if (customerSales.some(s => s.status === 'Pendente')) {
                    paymentStatus = 'Pendente';
                } else {
                    paymentStatus = 'Em Dia';
                }
            }
            return { ...customer, paymentStatus };
        });
        
        if (searchTerm) {
            items = items.filter(customer =>
                customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (customer.phone && customer.phone.includes(searchTerm)) ||
                (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        items.sort((a, b) => {
            const valA = a[sort.key] || '';
            const valB = b[sort.key] || '';

            if (sort.key === 'birthDate') {
                if (!valA) return 1;
                if (!valB) return -1;
                return sort.direction === 'asc' ? new Date(valA) - new Date(valB) : new Date(valB) - new Date(valA);
            }

            if (typeof valA === 'string') {
                return sort.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
            }
            
            if (valA < valB) return sort.direction === 'asc' ? -1 : 1;
            if (valA > valB) return sort.direction === 'asc' ? 1 : -1;
            return 0;
        });

        return items;
    }, [customers, sales, searchTerm, sort]);

    const paginatedCustomers = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredAndSortedCustomers.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredAndSortedCustomers, currentPage, itemsPerPage]);

    const handleItemsPerPageChange = useCallback((value) => {
        setItemsPerPage(Number(value));
        setCurrentPage(1);
    }, []);

    const isAllSelectedOnPage = useMemo(() => {
        if (paginatedCustomers.length === 0) return false;
        return paginatedCustomers.every(c => selectedIds.includes(c.id));
    }, [paginatedCustomers, selectedIds]);

    const handleScrollToTable = () => {
        tableRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleSort = useCallback((key) => {
        setSort(prevSort => ({
            key,
            direction: prevSort.key === key && prevSort.direction === 'asc' ? 'desc' : 'asc'
        }));
    }, []);

    const getStatusClass = (status) => {
        switch (status) {
            case 'Em Dia': return 'bg-green-100 text-green-800';
            case 'Pendente': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-[#F2DFDB]">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">Top 10 Clientes por Faturamento</h3>
                    {topCustomers.length > 0 ? (
                        <ul className="space-y-3">
                            {topCustomers.map((customer, index) => (
                                <li key={customer.id} className="flex justify-between items-center border-b border-[#F2DFDB] pb-2">
                                    <div className="flex items-center">
                                        <span className="text-lg font-bold text-[#D7BFBD] mr-4 w-6 text-center">{index + 1}</span>
                                        <button onClick={() => onCustomerSelect(customer.id)} className="font-medium text-gray-800 text-left hover:text-[#D7BFBD] transition-colors">
                                            {customer.name}
                                        </button>
                                    </div>
                                    <span className="font-semibold text-gray-800 bg-emerald-100 px-3 py-1 rounded-full">
                                        {formatCurrency(customer.totalRevenue)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-gray-500">Ainda não há dados de vendas para gerar o ranking de clientes.</p>
                    )}
                </div>
                <div className="space-y-6">
                    <button onClick={handleScrollToTable} className="text-left bg-white p-6 rounded-2xl shadow-sm border border-[#F2DFDB] w-full hover:bg-gray-50 transition-colors">
                      <h3 className="text-md font-medium text-[#9B9A98]">Total de Clientes</h3>
                      <p className="text-3xl font-bold text-gray-800 mt-2">{customers.length}</p>
                    </button>
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-[#F2DFDB]">
                        <h3 className="text-lg font-bold text-gray-800 mb-4">🎂 Aniversários (Próx. 30 dias)</h3>
                        {upcomingBirthdays.length > 0 ? (
                            <div>
                                <ul className="space-y-2">
                                    {paginatedBirthdays.map((customer) => (
                                         <li key={customer.id} className="flex justify-between items-center text-sm border-b border-[#F2DFDB] pb-1">
                                            <button onClick={() => onCustomerSelect(customer.id)} className="font-medium text-gray-700 hover:text-[#D7BFBD]">{customer.name}</button>
                                            <span className="font-bold text-gray-500">{new Date(customer.birthDate).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</span>
                                         </li>
                                    ))}
                                </ul>
                                {upcomingBirthdays.length > birthdaysPerPage && (
                                    <div className="flex justify-between mt-4">
                                        <button onClick={() => setBirthdayPage(p => Math.max(1, p - 1))} disabled={birthdayPage === 1} className="text-sm font-semibold disabled:opacity-50">Anterior</button>
                                        <span className="text-sm text-gray-500">{birthdayPage} de {Math.ceil(upcomingBirthdays.length / birthdaysPerPage)}</span>
                                        <button onClick={() => setBirthdayPage(p => Math.min(Math.ceil(upcomingBirthdays.length / birthdaysPerPage), p + 1))} disabled={birthdayPage === Math.ceil(upcomingBirthdays.length / birthdaysPerPage)} className="text-sm font-semibold disabled:opacity-50">Próximo</button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="text-gray-500 text-sm">Nenhum aniversário nos próximos 30 dias.</p>
                        )}
                    </div>
                </div>
            </div>

            <div ref={tableRef} id="gestao-clientes" className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-[#F2DFDB]">
                <h2 className="text-lg font-bold text-gray-800 mb-4">Gestão de Clientes</h2>
            <div className="p-4 mb-6 bg-rose-50 border border-rose-100 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm">
                <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${isLinkActive ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                <span className="text-sm font-bold text-gray-700">Link de Cadastro: {isLinkActive ? 'ATIVADO' : 'DESATIVADO'}</span>
                </div>
        
            <div className="flex items-center gap-2 w-full sm:w-auto relative">
            <button 
                onClick={() => handleToggleLink(!isLinkActive)}
                className={`flex-1 sm:flex-none px-4 py-2 rounded-xl text-xs font-black transition-all ${isLinkActive ? 'bg-white text-rose-500 border border-rose-200' : 'bg-rose-400 text-white'}`}
            >   
                {isLinkActive ? 'Desativar Link' : 'Ativar Link'}
            </button>

            {copiado && (
                <div className="absolute -top-10 right-0 bg-gray-800 text-white text-[10px] px-2 py-1 rounded-lg shadow-xl animate-bounce z-10">
                    Mensagem copiada! ✅
                     <div className="absolute bottom-[-4px] right-4 w-2 h-2 bg-gray-800 rotate-45"></div>
                </div>
            )}

            <button 
                onClick={handleShareWhatsApp}
                disabled={!isLinkActive}
                className="flex-1 sm:flex-none bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white px-5 py-2 rounded-xl text-xs font-black flex items-center justify-center gap-2 transition-all shadow-md"
            >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                </svg>

                WhatsApp
            </button>
            </div>
            </div>

                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-4">
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder="🔎 Pesquisar por nome, telefone ou email..."
                        className="w-full sm:w-1/2 px-4 py-2 bg-white border border-[#D7BFBD] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D7BFBD]"
                    />
                    <button onClick={() => openModal('addCustomer')} className="custom-button w-full sm:w-auto font-semibold py-2 px-4 rounded-lg shadow-sm text-sm">
                        + Adicionar Cliente
                    </button>
                </div>

                {selectedIds.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex justify-between items-center mb-4">
                        <span className="text-sm font-medium text-blue-800">{selectedIds.length} {selectedIds.length > 1 ? 'clientes selecionados' : 'cliente selecionado'}</span>
                        <button onClick={onDeleteSelected} className="flex items-center gap-2 text-sm font-semibold text-red-600 hover:text-red-800">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            Excluir Selecionados
                        </button>
                    </div>
                )}
                
                {/* START: Mobile View (Card List) */}
                <div className="space-y-4 md:hidden">
                    <div className="flex items-center justify-between p-2">
                        <label className="flex items-center text-sm font-medium">
                            <input 
                                type="checkbox" 
                                className="h-4 w-4 mr-2 rounded border-gray-300"
                                checked={isAllSelectedOnPage}
                                onChange={() => onSelectAll(paginatedCustomers.map(c => c.id), isAllSelectedOnPage)}
                            />
                            Selecionar Todos na Página
                        </label>
                    </div>
                    {paginatedCustomers.map(customer => (
                        <div key={customer.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
                            <div className="flex justify-between items-start">
                                <div className="flex items-start gap-3">
                                    <input 
                                        type="checkbox" 
                                        className="h-4 w-4 mt-1 rounded border-gray-300"
                                        checked={selectedIds.includes(customer.id)}
                                        onChange={() => onSelectOne(customer.id)}
                                    />
                                    {/* ALTERAÇÃO AQUI: Div envolvendo o Título e a Legenda do Endereço (Mobile) */}
                                    <div className="flex flex-col">
                                        <h3 
                                            className="font-bold text-gray-800 hover:text-[#D7BFBD] cursor-pointer" 
                                            onClick={() => onCustomerSelect(customer.id)}
                                        >
                                            {customer.name}
                                        </h3>
                                        {customer.address && (
                                            <span className="text-xs text-gray-400 mt-1 line-clamp-1 flex items-center gap-1" title={customer.address}>
                                                {customer.address}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button className="action-btn text-xl" onClick={() => onCustomerSelect(customer.id)} title="Ver Detalhes">👁️</button>
                                    <button className="action-btn edit-btn text-xl" onClick={() => openModal('editCustomer', customer)} title="Editar Cliente">✏️</button>
                                    <button className="action-btn delete-btn text-xl" onClick={() => openModal('confirmDelete', { id: customer.id, type: 'customer', name: customer.name })} title="Deletar Cliente">🗑️</button>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-center text-sm pt-3 border-t">
                                <div>
                                    <p className="font-semibold text-gray-500">Aniversário</p>
                                    <p className="text-gray-700">{customer.birthDate ? new Date(customer.birthDate).toLocaleDateString('pt-BR') : 'N/A'}</p>
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-500">Telefone</p>
                                    <p className="text-gray-700">{customer.phone || 'N/A'}</p>
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-500">Status</p>
                                    <p><span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusClass(customer.paymentStatus)}`}>{customer.paymentStatus}</span></p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                {/* END: Mobile View */}

                {/* START: Desktop View (Table) */}
                 <div className="overflow-x-auto hidden md:block">
                    <table className="w-full text-left">
                        <thead className="border-b-2 border-[#F2DFDB]">
                            <tr>
                                <th className="p-4 w-4"><input type="checkbox" className="h-4 w-4" checked={isAllSelectedOnPage} onChange={() => onSelectAll(paginatedCustomers.map(c => c.id), isAllSelectedOnPage)} /></th>
                                <th className="p-4 text-sm font-semibold sortable-header" onClick={() => handleSort('name')}>Nome {sort.key === 'name' && (sort.direction === 'asc' ? '▲' : '▼')}</th>
                                <th className="p-4 text-sm font-semibold sortable-header" onClick={() => handleSort('birthDate')}>Aniversário {sort.key === 'birthDate' && (sort.direction === 'asc' ? '▲' : '▼')}</th>
                                <th className="p-4 text-sm font-semibold sortable-header" onClick={() => handleSort('phone')}>Telefone {sort.key === 'phone' && (sort.direction === 'asc' ? '▲' : '▼')}</th>
                                <th className="p-4 text-sm font-semibold sortable-header" onClick={() => handleSort('paymentStatus')}>Status Pag. {sort.key === 'paymentStatus' && (sort.direction === 'asc' ? '▲' : '▼')}</th>
                                <th className="p-4 text-sm font-semibold text-gray-700 text-center">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedCustomers.map(customer => (
                                <tr key={customer.id} className="border-b border-[#F2DFDB] hover:bg-[#F4EFEC]">
                                    <td className="p-4"><input type="checkbox" className="h-4 w-4" checked={selectedIds.includes(customer.id)} onChange={() => onSelectOne(customer.id)} /></td>
                                    
                                    {/* ALTERAÇÃO AQUI: Célula expandida com Título e a Legenda do Endereço (Desktop) */}
                                    <td className="p-4 max-w-md">
                                        <div className="flex flex-col">
                                            <span 
                                                className="font-medium text-gray-800 hover:text-[#D7BFBD] cursor-pointer break-words line-clamp-2" 
                                                onClick={() => onCustomerSelect(customer.id)}
                                                title={customer.name}
                                            >
                                                {customer.name}
                                            </span>
                                            {customer.address && (
                                                <span className="text-xs text-gray-400 mt-1 flex items-center gap-1 line-clamp-1" title={customer.address}>
                                                    {customer.address}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    
                                    <td className="p-4 text-gray-600">{customer.birthDate ? new Date(customer.birthDate).toLocaleDateString('pt-BR') : 'N/A'}</td>
                                    <td className="p-4 text-gray-600">{customer.phone || 'N/A'}</td>
                                    <td className="p-4"><span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusClass(customer.paymentStatus)}`}>{customer.paymentStatus}</span></td>
                                    <td className="p-4 text-center">
                                        <button className="action-btn" onClick={() => onCustomerSelect(customer.id)} title="Ver Detalhes">👁️</button>
                                        <button className="action-btn edit-btn" onClick={() => openModal('editCustomer', customer)} title="Editar Cliente">✏️</button>
                                        <button className="action-btn delete-btn" onClick={() => openModal('confirmDelete', { id: customer.id, type: 'customer', name: customer.name })} title="Deletar Cliente">🗑️</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {/* END: Desktop View */}

                <Pagination 
                    totalItems={filteredAndSortedCustomers.length}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={handleItemsPerPageChange}
                />
            </div>
        </div>
    );
};

export default CustomersView;